import { createStore, compose, applyMiddleware } from 'redux';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import reduxImmutableStateInvariant from 'redux-immutable-state-invariant';
import thunk from 'redux-thunk';
import { createBrowserHistory } from "history";
import createRootReducer from '../reducers';
import api from '../helpers/api';
import * as types from '../constants/actionTypes';

export const history = createBrowserHistory();

// Enhancer to reset state on logout or token refresh failure
const resetEnhancer = (rootReducer) => (state, action) => {
    if ([types.LOGOUT_SUCCESS, types.TOKEN_REFRESH_FAILURE].includes(action.type)) {
        localStorage.removeItem('persist:root');
        return rootReducer(undefined, { type: '@@RESET' });
    }
    return rootReducer(state, action);
};

// Redux Persist configuration
const persistConfig = {
    key: 'root',
    storage,
    stateReconciler: autoMergeLevel2,
    blacklist: [], // Add reducers here to exclude them from persistence if needed
};

// Wrap root reducer with persist and reset enhancer
const persistedReducer = persistReducer(
    persistConfig,
    resetEnhancer(createRootReducer())
);

// Create production store
const configureStoreProd = (initialState) => {
    const middlewares = [thunk, api];
    const store = createStore(
        persistedReducer,
        initialState,
        compose(applyMiddleware(...middlewares))
    );
    const persistor = persistStore(store);

    return { store, persistor };
};

// Create development store
const configureStoreDev = (initialState) => {
    const middlewares = [
        reduxImmutableStateInvariant(), // Catch state mutations in development
        thunk,
        api,
    ];

    const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose; // Support Redux DevTools

    const store = createStore(
        persistedReducer,
        initialState,
        composeEnhancers(applyMiddleware(...middlewares))
    );

    // Enable hot module replacement (HMR) for reducers
    if (module.hot) {
        module.hot.accept('../reducers', async () => {
            const { default: newRootReducer } = await import('../reducers');
            store.replaceReducer(persistReducer(persistConfig, resetEnhancer(newRootReducer)));
        });
    }

    const persistor = persistStore(store);

    return { store, persistor };
};

// Choose appropriate configuration based on the environment
const configureStore = process.env.NODE_ENV === 'production' ? configureStoreProd : configureStoreDev;

export default configureStore;
